/**
 * @fileOverview The main entry point to your standalone. Start editing here.
 */

import React, { useState } from 'react'
import '@ta-interaktiv/semantic-ui/semantic/dist/components/reset.css'
import '@ta-interaktiv/semantic-ui/semantic/dist/components/site.css'
import '@ta-interaktiv/semantic-ui/semantic/dist/components/segment.css'
import '@ta-interaktiv/semantic-ui/semantic/dist/components/container.css'
import '@ta-interaktiv/semantic-ui/semantic/dist/components/header.css'
import '@ta-interaktiv/semantic-ui/semantic/dist/components/image.css'
import '@ta-interaktiv/semantic-ui/semantic/dist/components/list.css'
import './styles.scss'

import { AutofitIFrame } from '@ta-interaktiv/react-autofit-iframe'

// HOOKS
import { useWindowSize } from './useWindowSize'
import { useDimensions } from './useDimensions'
// KOMPONENTEN
import { Masthead } from '@ta-interaktiv/react-masthead'
import { Footer } from '@ta-interaktiv/react-footer'
import { ThemeSwitch } from '@ta-interaktiv/react-theme-switch'
import { Link, Image } from '@ta-interaktiv/contentbricks'
import { Square } from 'components/Square'
import { DatacenterPower } from 'components/DatacenterPower'
import { Waypoint } from 'react-waypoint'
import { SquareGrid } from 'components/SquareGrid'

// TODO Set the correct article ID here
const ARTICLE_ID = '966478951379'

// Workaround flickering Image
function Image2(props)
{
  return (<div className='cbImage oversized'>
    <figure>
      <div>
        <img src={props.file} style={{width: "100%"}} />
        <div className='source'>{props.source}</div>
      </div>
    </figure>

  </div>)
}

export function AppComponent() {
  const [waypointStatus, setWaypointStatus] = useState({
    newshown: false,
    plantsshown: false
  })
  const SIZE = useWindowSize()
  const GWGRID_CELLSIZE = SIZE.width < 712 ? 12 : 18
  const GWGRID_BREAKAFTER = 5
  const GWGRID_GROUPSPACE = 5
  const GWGRID_GROUPBREAK = SIZE.width < 712 ? 2 : 2
  const GWGRID_PROPS: GridProps = {
    cellsize: GWGRID_CELLSIZE,
    cellspace: 0,
    breackafter: GWGRID_BREAKAFTER,
    groupbreakafter: GWGRID_GROUPBREAK,
    cellcount: 25,
    groupspace: GWGRID_GROUPSPACE,
    cellcontent: <Square size={GWGRID_CELLSIZE} className='current' />
  }

  const waypointtrigger = (e: any, prop: string) => {
    if (e.currentPosition === 'inside' && e.previousPosition === 'below')
      setWaypointStatus({ ...waypointStatus, [prop]: true })
    if (e.currentPosition === 'below' && e.previousPosition === 'inside')
      setWaypointStatus({ ...waypointStatus, [prop]: false })
  }

  const currentdatacenters = [
    { name: 'Equinix ZH2', power: 0.5 },
    { name: 'colt DCS', power: 4 },
    { name: 'Equinix ZH4', power: 2.5 },
    { name: 'Interxion ZUR 1', power: 5 },
    { name: 'Interxion ZUR 2', power: 12 },
    { name: 'Equinix ZH5', power: 14 },
    { name: 'NTT 1', power: 15 },
    { name: 'GTT', power: 1 },
    { name: 'Swisscom (Herdern)', power: 8 },
    // { name: 'IBM (Winterthur)', power: 3 },
    { name: 'IBM (Kloten)', power: 1.3 },
    { name: 'OIZ (Albis)', power: 6 },
    { name: 'OIZ (Hagenholz)', power: 1.3 },
    // { name: 'Green Zürich-West 3', power: 27 }
  ]

  const newdatacenters = [
    { name: 'NTT 2', power: 15 },
    { name: 'Safe Host', power: 20 },
    { name: 'Interxion ZUR 3', power: 24 },
    { name: 'Equinix 5.4-5.6', power: 26 },
    { name: 'Green Metro-Campus', power: 50 },
    { name: 'Vantage DC', power: 55 }
  ]

  const powersupplies = [
    { name: 'Kraftwerk Eglisau-Glattfelden', power: 44 },
    { name: 'Limmatkraftwerk Wettingen', power: 26 },
    { name: 'Limmatkraftwerk Dietikon', power: 3.5 },
    { name: 'Wasserkraftwerk Tinizong', power: 69 },
    { name: 'Windpark Måkaknuten (Nor)', power: 95 },
    { name: 'Windpark Stigafjellet (Nor)', power: 30 },
    // { name: 'Kernkraftwerk Leibstadt', power: 35, powerprefix: "Beteiligung EWZ:" },
  ]  
  const [stickyel, stickyelsize, Node] = useDimensions()
  const [stickyel2, stickyelsize2, Node2] = useDimensions()
  return (
    <div className='index'>
      <Masthead articleId={ARTICLE_ID} />
      <ThemeSwitch />

      <header className='ui vertical  very fitted  segment'>
        <div className='ui container'>
          <div className='ui header'>
            <h1>Diese Rechenzentren brauchen fast so viel Strom wie eine Kleinstadt</h1>
            <h2>
              In Zürich schiessen riesige Rechenzentren wie Pilze aus dem Boden. Wie viel Strom diese verbrauchen werden, ist nicht bekannt. Wir haben nachgerechnet.
            </h2>
          </div>
        </div>
      </header>

      <div className='ui vertical segment a'>
        <div className='ui text container'>
          <div className='ta metainfo'>
            <div className='ta author'>Simon Huwiler, Sebastian Broschinski</div>
            {/* <div className='ta updated'>Aktualisiert am 31. XXXXXXX 2020</div> */}
          </div>
          <div className='ta text '>
            <p>
              Das Internet ist gut 70 Meter lang, 50 Meter breit, mehrere Stockwerke hoch und steht in Rümlang. Oder in Zürich, Schlieren, Oberengstringen. Überall füttern riesige Rechenzentren das Netz. Und der Zürcher Teil des Internets wächst rasant, im ganzen Kanton wird gebaut und geplant auf Teufel komm raus. 
            </p>

            <p>
              Die Betreiber nutzen ihre Gebäude nicht selbst, sondern vermieten sie weiter. «Colocation» nennt sich dies, und es ist kein neues Phänomen. Dass jetzt die Grossen ausbauen und zusätzlich ausländische Firmen nach Zürich drängen, hat zum Teil mit dem Corona-bedingten Digitalisierungsschub zu tun. Vor allem aber haben grosse Cloudanbieter die Schweiz entdeckt und suchen Unterschlupf für ihre Server (<Link
                href='https://www.tagesanzeiger.ch/warum-in-zuerich-rechenzentren-wie-pilze-aus-dem-boden-schiessen-866097225978'
                allowWordWrap
              >Lesen Sie hier mehr zu den Hintergründen</Link>).
            </p>

            <AutofitIFrame src='//datawrapper.dwcdn.net/KdopQ/' />

            <h4>Was auf Zürich zukommen könnte</h4>
            <p>
              Weil das Mehl dieser Internetbäcker der Strom ist, wachsen gerade riesige Stromfresser heran. Offiziell gibt es kaum Zahlen dazu. Die letzte landesweite Erhebung liegt sieben Jahre zurück. 2014 kam das Bundesamt für Energie zum Ergebnis, dass rund 2,8 Prozent des Schweizer Stromverbrauchs auf Rechenzentren zurückzuführen sei. Die Studie wird jetzt aktualisiert, im April sollen neue Ergebnisse vorliegen. 
            </p>
            <p>
              Verschiedene Experten, mit denen diese Zeitung gesprochen hat, schätzen die Zunahme auf etwa 30 Prozent. Rechenzentren werden also knapp 4 Prozent am gesamten Stromverbrauch ausmachen. Das ist überschaubar, doch der grosse Ausbau hat eben erst begonnen.
            </p>

            <p>
              Wie viel Strom die bestehenden und neuen Zürcher Rechenzentren jährlich verbrauchen, darüber schweigen Betreiber und Energiewerke. Wir wagen aber eine Prognose, denn Hinweise finden sich in Kundendokumentationen, Baugesuchen oder können aus der Grösse der Notstromaggregate abgeleitet werden.
            </p>

            <div className="box">
            <p>
              <b>Was ist ein Megawatt?</b><br />
              Ein Megawatt (=1000 Kilowatt = 1’000’000 Watt) bezeichnet, welche Leistung ein Gerät beziehen kann (oder muss), jedoch nicht den effektiven Stromverbrauch. Erst mit der Dauer lässt sich der Stromverbrauch ableiten. Ein Beispiel: Läuft das Rechenzentrum mit einer Leistung von einem Megawatt während zwei Stunden unter Volllast, hat es 2 Megawattstunden (MWh) Strom verbraucht.
            </p>
            <p>
              Eine Heizung eines Einfamilienhauses hat 10 bis 20 kW Leistung, ein Vierpersonenhaushalt zwischen 500 und 1000 Watt, ein Notebook um die 50 Watt und eine Energiesparlampe zwischen 5 und 25 Watt.
            </p>
            </div>

            <p>
              Diese Werte sind theoretisch, denn Rechenzentren müssen für kurze, extreme Spitzen ausgelegt sein und laufen im Normalbetrieb nicht unter Volllast. Auch das Wetter spielt eine grosse Rolle: In heissen Sommermonaten erfordert die Kühlung mehr Energie als im Winter.
            </p>
          </div>
          {/**
           *
           *
           *
           * AUFSCHLÜSSELUNG  BESTEHENDE
           *
           *
           */}
          <div className='mapkey'>
            <div className='maptitle'>Bestehende Zürcher Rechenzentren</div>
            <div className='mapkeyentry'>
              <svg>
                <Square size={13} className='current' />
              </svg>
              Leistung in MW
            </div>
          </div>
          <div className='itemized'>
            {currentdatacenters
              .sort((a, b) => b.power - a.power)
              .map((dc, i) => (
                <DatacenterSolo
                  name={dc.name}
                  power={dc.power}
                  gridprops={GWGRID_PROPS}
                  isLeft={false}
                  space={0}
                />
              ))}
            <HorizontalBracket direction='bottom' center={0.4} />
          </div>
          {/**
           *
           *
           *
           * VERGLEICH VON BESTEHENDEN, NEUEN UND DER PRODUKTION
           *
           *
           */}
          <div className='ta text stickycontainer'>
            <div className='stickyholder'>
              <svg className='stickysvg' ref={stickyel}>
                <MovedGroup x={stickyelsize.width / 2} y={150}>
                  <DatacenterPower
                    gridprops={GWGRID_PROPS}
                    power={70}
                    name={['Anschlussleistung grosser', 'bestehenden Rechenzentren']}
                    direction='rl'
                    isMobile={SIZE.isMobile}
                  />
                </MovedGroup>
                {waypointStatus.newshown && (
                  <MovedGroup x={stickyelsize.width / 2 + 20} y={150}>
                    <DatacenterPower
                      gridprops={{
                        ...GWGRID_PROPS,
                        cellcontent: (
                          <Square size={GWGRID_CELLSIZE} className='new' />
                        )
                      }}
                      power={190}
                      name={['Anschlussleistung der', 'geplanten Rechenzentren']}
                      isMobile={SIZE.isMobile}
                    />
                  </MovedGroup>
                )}

                {waypointStatus.plantsshown && (
                  <MovedGroup
                    x={stickyelsize.width / 2}
                    y={SIZE.width > 711 ? 435 : 370}
                  >
                    <DatacenterPower
                      gridprops={{
                        ...GWGRID_PROPS,
                        cellcontent: (
                          <Square size={GWGRID_CELLSIZE} className='plants' />
                        )
                      }}
                      power={40}
                      name={[
                        'Durchschnittliche Leistung',
                        'der 4 Zürcher Kraftwerke'
                      ]}
                      direction='rl'
                      isMobile={SIZE.isMobile}
                    />
                  </MovedGroup>
                )}
              </svg>
            </div>
            {/**
             *
             *
             *
             * TEXTE ZUM VERGLEICH
             *
             *
             */}
            <div
              className='ui raised segment textoverlay'
              style={{ marginBottom: '100vh' }}
            >
              So sieht es momentan im Kanton aus. Die grössten Zürcher Rechenzentren können eine maximale Leistung von ungefähr 70 Megawatt in Anspruch nehmen. Die dominanten Player sind: Das Schweizer Unternehmen Green im aargauischen Lupfig, mit Satelliten in Schlieren und Glattbrugg. Equinix im Jumbogebäude beim Escher-Wyss-Platz in Zürich sowie in Oberengstringen. Interxion mit zwei Rechenzentren in Glattbrugg. Und NTT in Rümlang.
            </div>
            <Waypoint
              topOffset='-200%'
              onEnter={e => waypointtrigger(e, 'newshown')}
              onLeave={e => waypointtrigger(e, 'newshown')}
            >
              <div
                className='ui raised segment textoverlay'
                style={{ marginBottom: '100vh' }}
              >
                Werden in wenigen Jahren alle jetzt bekannten Baupläne umgesetzt sein, haben die neuen Rechenzentren mehr als doppelt so viel Leistung zur Verfügung wie alle bestehenden, grossen zusammen. Rund 190 Megawatt könnten diese gleichzeitig beziehen. Wenn alle Zentren gleichzeitig die ihnen zur Verfügung stehende Leistung nutzen würden, entspräche das einem Viertel des Stromverbrauchs der Stadt Zürich.
              </div>
            </Waypoint>
            

            {/* <Waypoint
              topOffset='-200%'
              onEnter={e => waypointtrigger(e, 'plantsshown')}
              onLeave={e => waypointtrigger(e, 'plantsshown')}
            >
              <div
                className='ui raised segment textoverlay'
                style={{ marginBottom: '100vh' }}
              >
                {' '}
                In Winterthur etwa sollen drei Rechenzentren des Holländischen? Unternehmens Vantage DC entstehen. Der Rohbau des ersten Gebäudes ist fertig. Bekannt ist, dass die Anlage über eine Anschlussleistung von 55 MW verfügt. In der gleichen Grössenordnung: Der Metro-Campus von Green in Dielsdorf mit drei Gebäuden. Fertigstellung der ersten Bauphase: März 2022. In Rafz baut Safe Host ein Rechenzentrum wahrscheinlich für Microsoft. Bereits werden Spezialisten gesucht, der Ausbau läuft. Auch die die bereits in der Schweiz ansässigen Equinix (erweitert ihres bestehendes Rechenzentrums ZH 5 um drei Etappen), Interxion und NTT/e-shelter sind am bauen.
              </div>
            </Waypoint> */}
          </div>

          
          <div className='ta text stickycontainer'>
            <div className='stickyholder' ref={stickyel2}>
              <div className='stickysvgXX'>
              {/**
               *
               *
               *
               * AUFSCHLÜSSELUNG  NEUE
               *
               *
               */}
              <HorizontalBracket direction='top' center={0.6} />

              <div className='mapkey'>
                <div className='maptitle'>Geplante Rechenzentren</div>
                <div className='mapkeyentry'>
                  <svg>
                    <Square size={13} className='new' />
                  </svg>
                  Stromverbrauch in MW
                  {/* <span style={{color: 'grey'}}>
                  1 MW ist etwa gleich viel wie die mittlere Leistungsaufnahme von 1800 2-Personen-Wohnungen
                  </span> */}
                </div>
              </div>
              <div className='itemized'>      
                {newdatacenters
                  .sort((a, b) => b.power - a.power)
                  .map((dc, i) => (
                    <DatacenterSolo
                      name={dc.name}
                      power={dc.power}
                      gridprops={{
                        ...GWGRID_PROPS,
                        cellcontent: (
                          <Square size={GWGRID_CELLSIZE} className='new' />
                        )
                      }}
                      isLeft={false}
                      space={0}
                    />
                  ))}
                </div>



                {waypointStatus.plantsshown && (
                  <>
                    <div className='mapkey'>
                      <div className='maptitle'>Ausgewählte Kraftwerke mit Zürcher Beteiligung</div>
                      <div className='mapkeyentry'>
                        <svg>
                          <Square size={13} className='plants' />
                        </svg>
                        Maximale Leistung
                      </div>
                    </div>                  
                    <div className='itemized'>

                      {powersupplies
                        .sort((a, b) => b.power - a.power)
                        .map((dc, i) => (
                          <DatacenterSolo
                            name={dc.name}
                            power={dc.power}
                            powerprefix={dc.powerprefix}
                            gridprops={{
                              ...GWGRID_PROPS,
                              cellcontent: (
                                <Square size={GWGRID_CELLSIZE} className='plants' />
                              )
                            }}
                            isLeft={false}
                            space={0}
                            heightincells={12}
                          />
                        ))}
                    </div>
                 </>)}          
              </div>

           </div> 
          
            <div
              className='ui raised segment textoverlay'
              style={{ marginBottom: '100vh' }}
            >
              Unter den Bauprojekten finden sich altbekannte und neue Mitbewerber. In Winterthur etwa sollen drei Rechenzentren des US-Unternehmens Vantage DC entstehen. In der gleichen Grössenordnung: der Metro-Campus von Green in Dielsdorf. Das erste von drei Gebäuden soll im März 2022 fertig sein. In Rafz steht ein Zentrum der Firma Safe Host kurz vor der Fertigstellung. Equinix baut ihr bestehendes Rechenzentrum in Oberengstringen in drei Etappen aus, und auch Interxion (Glattbrugg) und NTT (Rümlang) sind am Bauen.
            </div>
            <Waypoint
              topOffset='-200%'
              onEnter={e => waypointtrigger(e, 'plantsshown')}
              onLeave={e => waypointtrigger(e, 'plantsshown')}
            >
              <div
                className='ui raised segment textoverlay'
                style={{ marginBottom: '100vh' }}
              >
                <p>
                Der Strombedarf wird fassbarer bei einem Vergleich mit Zürcher Kraftwerken. Kleine Flusskraftwerke wie etwa das Limmatkraftwerk Dietikon (3,5 MW) sind da nur einen Tropfen auf den heissen Stein. Das Limmatkraftwerk in Wettingen (26 MW) könnte den Energiehunger eines unter Volllast laufenden Winterthurer Campus knapp zur Hälfte decken. Erst grössere Kraftwerke, etwa das EWZ-Wasserkraftwerk Castasegna im Bergell (100 MW) oder der norwegische EWZ-Windpark Måkaknuten mit seinen 22 Windturbinen (95 MW), produzieren genügend Energie. Vorausgesetzt, Wind und Wasser sind vorhanden.
                </p>
              </div>
            </Waypoint>            

            {/* <div
                className='ui raised segment textoverlay'
                style={{ marginBottom: '100vh' }}
              >
                {' '}
                XXX
              </div>           */}
              

        </div>
      </div>
          </div> 

      <div className='ui vertical segment'>
        <div className='ui text container'>
          <div className='ta text'>
            <h4>Das Netz muss ausgebaut werden</h4>

            <p>
              Der steigende Energieverbrauch beschäftigt auch die Elektrizitätswerke. Nicht wegen des Strombedarfs, den können sie decken, sondern wegen ihrer Infrastruktur. Das Netz sei nicht für solche Hotspots ausgelegt und müsse entsprechend verstärkt werden, sagt Christian Schwarz von den Elektrizitätswerken des Kantons Zürich (EKZ). «Aber der Energiehunger rund um die Stadt Zürich wächst auch stetig. Deshalb kommt es häufig zu einer Symbiose beim Netzausbau.»
            </p>

            <Image2 file={require('../../images/ntt.jpg')} source="Hinter dem aktuellen Rechenzentrum von NTT in Rümlang wird gerade gebaut. Dafür musste die EKZ ihr Netz ausbauen. Foto: Boris Mueller" oversized={true}/>

            <p>
              Mehrere Unterwerke haben die EKZ wegen Rechenzentren neu gebaut oder verstärkt, etwa in Rümlang, Oberengstringen und Rafz. In Glattbrugg und Dielsdorf wird nächstens ebenfalls in die Netzinfrastruktur investiert. Auch die Stadtwerke Winterthur haben für das entstehende Rechenzentrum ein Unterwerk verstärkt. Ein Teil der Investitionen werde den Betreibern weiterverrechnet, teilen die EKZ mit.
            </p>
            <h4>Rechenzentren wollen grüner werden</h4>
            <p>
              In Zeiten von Klimastreiks und CO<sub>2</sub>-Zielen stehen solch grosse Stromverbraucher quer in der Landschaft. Beim Weltmarktführer Equinix mit Rechenzentren in Zürich und Genf ist man sich dessen bewusst: «Nachhaltigkeit ist für uns ein zentrales Thema», sagt Schweiz-Chef Roger Semprini. «Bis 2030 sollen alle unsere Rechenzentren CO₂-neutral arbeiten.» Zusammen mit Mitbewerbern hat das Unternehmen Anfang des Jahres den europäischen «Pakt für klimaneutrale Rechenzentren» verfasst. «In der Schweiz beziehen wir bereits heute 100 Prozent  unseres Stroms aus erneuerbaren Energien», sagt Semprini. Zudem würden grosse Rechenzentren weniger Strom verbrauchen, als wenn jedes Unternehmen seine Server selber betreiben würde.
            </p>

            <Image2 file={require('../../images/equinix.jpg')} source="In Oberengstringen hat die EKZ gleich neben dem Rechenzentrum von Equinix eine Travostation gebaut. Foto: Boris Mueller" oversized={true}/>

            <p>
              Diese Bestrebungen sind kein Lippenbekenntnis. Tatsächlich erhöhen fortschrittlichere Kühlmethoden Jahr für Jahr die Effizienz. Abwärme wird genutzt, um Wohnungen oder Schwimmbäder zu heizen. In Luzern entsteht gerade ein Rechenzentrum, das mit Seewasser kühlt. Green in Lupfig lässt in der Nacht Wasser auskühlen, das am Tag zur Hitzereduktion eingesetzt werden kann. Die Hochschule in Luzern kühlt ihr eigenes Rechenzentrum ohne klassische Kältemaschinen und nutzt den Verdunstungseffekt. Und Microsoft hat vor ein paar Jahren ein Rechenzentrum zu Testzwecken gleich ganz im Meer versenkt.
            </p>

            <p>
              Läuft also alles prima? Noch nicht ganz zufrieden mit den hiesigen Rechenzentren ist Adrian Altenburger, Professor für Gebäudetechnik und Energie an der Hochschule Luzern und Gründungsmitglied der Swiss Datacenter Efficiency Association: «Im internationalen Vergleich steht die Schweiz nicht schlecht da», sagt er. Die klimatischen Bedingungen – also die relativ tiefen Temperaturen – seien eine gute Voraussetzung für die effiziente Kühlung. «Wir neigen in der Schweiz jedoch zu Copy-paste». Will heissen: Was funktioniert, wird erneut gemacht, auf die Erkenntnisse aus der Forschung will man sich jedoch oft nicht einlassen. 
            </p>

            <h4>Zu geringer Anreiz</h4>

            <p>
              Ein Problem sieht Altenburger bei seinen Ingenieur-Berufskolleginnen und -kollegen: «Ingenieure werden meist in Relation zur Investitionssumme entlöhnt. Entwickeln sie energieeffizientere Lösungen, die zum Beispiel die teuren Kältemaschinen unnötig machen, strafen sie sich selbst.» Grösste Wirkung würde man erzielen, wenn man moderate Vorschriften wie im Gebäudebereich auch für Rechenzentren machen würde. Dies sei aber nicht so einfach umsetzbar, weil es Sache der Kantone sei, eine vergleichsweise geringe Objektzahl betreffe und ein gewisses Mass an Sachkenntnis voraussetze.
            </p>

            <Image2 file={require('../../images/interxion.jpg')} source="Das zweite Rechenzentrum ZUR2 von Interxion. Daneben wird bereits das nächste gebaut. Foto: Boris Mueller" oversized={true}/>

            <p>
              Gelegenheiten zum Energiesparen werden die Ingenieure noch genügend haben, denn ein Ende des Baubooms ist nicht zu erkennen. Wie es dereinst in der Schweiz aussehen könnte, verrät ein Blick über den Tellerrand. Irland, ein beliebter Standort für Datenzentren, rechnet damit, dass diese bis 2029 für 27 Prozent des Energiebedarfs verantwortlich sein werden. In Dänemark könnten es bis 2030 15 Prozent sein.
            </p>

          </div>
        </div>
      </div>

      <Footer 
        articleId={ARTICLE_ID} 
        credits={[['Text', 'Simon Huwiler'], ['Entwicklung', 'Sebastian Broschinski']]} 
        hidePortfolioTeaser
        hideUserFeedback
        />
    </div>
  )
}
const HorizontalBracket = ({
  radius = 50,
  center,
  width,
  direction = 'bottom'
}: {
  radius?: number
  center?: number
  width?: number
  direction?: 'top' | 'bottom'
}) => {
  const [REF, size] = useDimensions()
  const w = width || size.width
  const c = w * (center || 0.5)
  const r = radius

  const d_bottom = `M${w},0A${r / 2},${r / 2},0,0,1,${w - r / 2},${r / 2}H${c +
    r / 2}A${r / 2},${r / 2},0,0,0,${c},${r},${r / 2},${r / 2},0,0,0,${c -
    r / 2},${r / 2}H${r / 2}A${r / 2},${r / 2},0,0,1,0,0`

  const d_top = `M0,${r}A${r / 2},${r / 2},0,0,1,${r / 2},${r / 2}H${c -
    r / 2}A${r / 2},${r / 2},0,0,0,${c},0,${r / 2},${r / 2},0,0,0,${c +
    r / 2},${r / 2}H${w - r / 2}a${r / 2},${r / 2},0,0,1,${r / 2},${r / 2}`

  return (
    <svg ref={REF} className='HorizontalBracket' height={r}>
      <path d={direction === 'top' ? d_top : d_bottom} />
    </svg>
  )
}
const MovedGroup = ({
  x,
  y,
  children
}: {
  x: number
  y: number
  children?: JSX.Element | string | undefined
}) => <g transform={`translate(${x},${y})`}>{children && children}</g>

const DatacenterSolo = ({
  name,
  power,
  powerprefix,
  gridprops,
  isLeft,
  heightincells = 7,
  space = 0
}: {
  name: string
  power: number
  powerprefix: any
  space: number
  gridprops: GridProps
  heightincells: number
  isLeft: boolean
}) => {
  const [REF, size, a] = useDimensions()
  const propsLeft = {
    textAnchor: 'end',
    x: size.width - space,
    direction: 'rl'
  }
  const propsRight = { textAnchor: 'start', x: space }
  const props = isLeft ? propsLeft : propsRight

  return (
    <svg
      className='onedatacenter'
      height={50 + gridprops.cellsize * heightincells}
      ref={REF}
    >
      <text className='title' y={20} {...props}>
        {name}
      </text>
      <text className='power' y={40} {...props}>
        {powerprefix ? powerprefix : ''} {power} MW
      </text>
      <MovedGroup
        y={50}
        {...props}
        x={isLeft ? size.width - space - gridprops.cellsize : space}
      >
        <SquareGrid {...gridprops} cellcount={power} {...props} />
      </MovedGroup>
    </svg>
  )
}
